import { EventDataFragment } from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import {
  AlertDialog,
  CircleIcon,
  Color,
  DeleteIcon,
  EditIcon,
  MoreHorizIcon,
  Popover,
  PushPinIcon,
  TextField,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Typography,
  makeStyles
} from "@material-ui/core";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDeleteConsultantEventLogEntry } from "../hooks/delete-consultant-event-log-entry";
import { useUpdateConsultantEventLogEntry } from "../hooks/update-consultant-event-log-entry";
import { useUpdateConsultantEventLogPin } from "../hooks/update-consultant-event-log-pin";

export type EventEntryProps = {
  icon: React.ReactNode;
  event: EventDataFragment;
  content: (description: string) => React.ReactNode;
  onUpdated?: () => void;
};

export const EventEntry = ({ icon, event, content, onUpdated }: EventEntryProps) => {
  const classes = useStyles({ sticky: event.Sticky });
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(event.Description);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>();
  const { showError, showSuccess } = useSnackbarsPipeline();

  const onError = () => {
    showError(locale.cannotPerformAction);
    setLoading(false);
  };

  const onCompleted = () => {
    onUpdated?.();
  };

  const { updateConsultantEventLogPin } = useUpdateConsultantEventLogPin({
    onError,
    onCompleted
  });

  const { deleteConsultantEventLogEntry } = useDeleteConsultantEventLogEntry({
    onError,
    onCompleted: () => {
      showSuccess(locale.Deleted);
      onCompleted();
    }
  });

  const { updateConsultantEventLogEntry } = useUpdateConsultantEventLogEntry({
    onError,
    onBeforeCall: () => setLoading(true),
    onCompleted: () => {
      showSuccess(locale.Updated);
      setLoading(false);
      onCompleted();
    }
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef.current, editing]);

  const renderContent = () => {
    if (loading) {
      return <CircularProgress style={{ margin: "auto" }} />;
    } else if (editing) {
      return (
        <ClickAwayListener
          onClickAway={() => {
            setEditing(false);
            if (description === event.Description) return;
            updateConsultantEventLogEntry({
              eventId: event._id,
              desc: description,
              event
            });
          }}
        >
          <TextField
            inputRef={inputRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth={true}
            multiline={true}
          />
        </ClickAwayListener>
      );
    } else {
      return content(description);
    }
  };

  return (
    <div>
      <Box className={classes.root}>
        <div style={{ zIndex: 1 }}>{icon} </div>
        <div className={classes.commentContainer}>
          <Box className={classes.topContainer}>
            <Box className={classes.textContainer}>
              <Typography variant="subtitle1">{event.CreatedBy.Name}</Typography>
              {event.CreatedOn && (
                <>
                  <CircleIcon
                    htmlColor={Color.DetailsInput}
                    style={{ width: "4px", height: "4px" }}
                  />
                  <Typography variant="subtitle1">
                    {moment(event.CreatedOn).format("DD MMMM YYYY HH:mm")}
                  </Typography>
                </>
              )}
            </Box>
            <Box className={classes.iconContainer}>
              {(event.CanDelete || event.CanEdit) && (
                <IconButton
                  className={classes.iconButton}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <MoreHorizIcon />
                </IconButton>
              )}
              {event.CanPin && (
                <IconButton
                  className={classes.iconButton}
                  onClick={() => updateConsultantEventLogPin({ event })}
                >
                  <PushPinIcon
                    htmlColor={event.Sticky ? Color.PrimaryMain : Color.LightGray}
                  />
                </IconButton>
              )}
            </Box>
          </Box>
          {renderContent()}
          <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
          >
            <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
              <div>
                <Box
                  className={classes.popoverButtonContainer}
                  onClick={() => {
                    setEditing(true);
                    setAnchorEl(null);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <IconButton style={{ padding: "0.25rem" }}>
                    <EditIcon htmlColor={Color.Body} />
                  </IconButton>
                  <Typography variant="body1">{locale.Edit}</Typography>
                </Box>
                <Box
                  className={classes.popoverButtonContainer}
                  onClick={() => {
                    setDeleteDialogOpen(true);
                    setAnchorEl(null);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <IconButton style={{ padding: "0.25rem" }}>
                    <DeleteIcon htmlColor={Color.Body} />
                  </IconButton>
                  <Typography variant="body1">{locale.Delete}</Typography>
                </Box>
              </div>
            </ClickAwayListener>
          </Popover>
        </div>
      </Box>

      <AlertDialog
        open={deleteDialogOpen}
        title={locale.DeleteConfirmation}
        onOk={() => deleteConsultantEventLogEntry({ event })}
        onClose={() => setDeleteDialogOpen(false)}
      />
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem"
  },
  topContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    columnGap: "2rem"
  },
  textContainer: {
    display: "flex",
    flexDirection: "row",
    columnGap: "0.25rem",
    alignItems: "center"
  },
  iconButton: {
    padding: "0"
  },
  iconContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  commentContainer: (props: { sticky: boolean }) => ({
    width: "calc(100% - 3.5rem)",
    position: "relative",
    padding: "0.5rem 0.5rem 0.5rem 0.625rem",
    backgroundColor: props.sticky ? Color.LightYellow : Color.White,
    boxShadow: `1px 6px 24px 0px ${Color.BoxShadow}`,
    border: `1px solid ${theme.palette.info.light}`,
    borderRadius: "0.125rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",

    "&::after, &::before": {
      right: "100%",
      top: "1rem",
      border: "solid transparent",
      content: '""',
      height: "0",
      width: "0",
      position: "absolute",
      pointerEvents: "none"
    },
    "&::after": {
      borderColor: "transparent",
      borderRightColor: props.sticky ? Color.LightYellow : Color.White,
      borderWidth: "4px",
      marginTop: "-4px"
    },
    "&::before": {
      borderColor: "transparent",
      borderRightColor: theme.palette.info.light,
      borderWidth: "6px",
      marginTop: "-6px"
    }
  }),
  popoverButtonContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "0.5rem",
    alignItems: "center"
  }
}));
